import React, { FC, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { priceRequestCounterpartsTemplate, priceRequestsTemplate } from 'apollo/templates/price-request';
import { PriceRequestCounterpart, PriceRequestPage, PriceRequestStatus } from 'models/price-request';
import { permissions } from 'utils/permission';
import CounterpartAutocomplete from 'components/price-requests/request/counterpart-autocomplete.component';
import PriceRequestLogTable, {
    ROWS_PER_PAGE_OPTIONS,
} from 'components/price-requests/request/price-request-log-table.component';
import NewPriceRequestDialog from 'components/price-requests/request/new-price-request-dialog.component';

const ALL = '';

/**
 * DD-3697: the request log. Both sides use this page — the chain at /price-requests, the lab as
 * its customer list's Price adjustments tab (DD-3840); the backend scopes the requests to the
 * caller's organization and hides the other side's drafts. `?counterpart=<id>` preselects
 * the filter (the chain's Labs > edit lab page links here).
 */
const PriceRequests: FC = () => {
    const { t: translate } = useTranslation('price-requests');
    const navigate = useNavigate();
    const currentUser = useReactiveVar(currentUserVar);
    const isChain = permissions.isDentist(currentUser?.type);
    const [searchParams, setSearchParams] = useSearchParams();
    const counterpartId = searchParams.get('counterpart') ?? ALL;
    const [status, setStatus] = useState<PriceRequestStatus | typeof ALL>(ALL);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
    const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);

    const counterparts = useQuery<{ priceRequestCounterparts: PriceRequestCounterpart[] }>(
        priceRequestCounterpartsTemplate,
        { fetchPolicy: 'cache-and-network' },
    );
    const {
        data,
        loading: isLoading,
        error,
        refetch,
    } = useQuery<{ priceRequests: PriceRequestPage }>(priceRequestsTemplate, {
        variables: {
            counterpartId: counterpartId || undefined,
            statuses: status ? [status] : undefined,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        },
        fetchPolicy: 'cache-and-network',
    });

    const changeCounterpart = (value: string) => {
        setPage(0);
        setSearchParams(value ? { counterpart: value } : {});
    };

    const isFiltered = Boolean(counterpartId || status);
    const counterpartList = counterparts.data?.priceRequestCounterparts ?? [];
    const requests = data?.priceRequests.items ?? [];

    const renderLog = () => {
        if (error) {
            return (
                <Alert
                    severity="error"
                    action={<Button onClick={() => refetch()}>{translate('RETRY')}</Button>}
                    data-test="price-requests.error"
                >
                    {translate('ERROR_LOADING_REQUESTS')}
                </Alert>
            );
        }
        if (isLoading && !data) return <Loader />;
        if (!requests.length) {
            return (
                <Paper sx={{ p: 3 }} data-test="price-requests.empty">
                    <Typography color="text.secondary">
                        {translate(isFiltered ? 'NO_REQUESTS_FILTERED' : 'NO_REQUESTS')}
                    </Typography>
                </Paper>
            );
        }
        return (
            <PriceRequestLogTable
                requests={requests}
                total={data?.priceRequests.total ?? 0}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onRowsPerPageChange={(value) => {
                    setRowsPerPage(value);
                    setPage(0);
                }}
            />
        );
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <PageTitle headingStyle="h1" title={translate('PRICE_REQUESTS')} />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setIsNewRequestOpen(true)}
                    data-test="price-requests.new"
                >
                    {translate('NEW_REQUEST')}
                </Button>
            </Box>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <Box sx={{ minWidth: 280 }}>
                    <CounterpartAutocomplete
                        size="small"
                        label={translate(isChain ? 'LAB' : 'CHAIN')}
                        placeholder={translate(isChain ? 'ALL_LABS' : 'ALL_CHAINS')}
                        counterparts={counterpartList}
                        value={counterpartId}
                        onChange={changeCounterpart}
                        dataTest="price-requests.counterpart-filter"
                    />
                </Box>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="price-requests-status">{translate('STATUS')}</InputLabel>
                    <Select
                        labelId="price-requests-status"
                        label={translate('STATUS')}
                        value={status}
                        onChange={(event) => {
                            setPage(0);
                            setStatus(event.target.value as PriceRequestStatus | typeof ALL);
                        }}
                        data-test="price-requests.status-filter"
                    >
                        <MenuItem value={ALL}>{translate('ALL_STATUSES')}</MenuItem>
                        {Object.values(PriceRequestStatus).map((value) => (
                            <MenuItem key={value} value={value}>
                                {translate(`STATUS_${value}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            {counterparts.error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('ERROR_LOADING_COUNTERPARTS')}
                </Alert>
            ) : undefined}
            {renderLog()}
            {isNewRequestOpen ? (
                <NewPriceRequestDialog
                    open
                    isChain={isChain}
                    initialCounterpartId={counterpartId || undefined}
                    onClose={() => setIsNewRequestOpen(false)}
                    onCreated={(request) => navigate(`/price-requests/${request._id}`)}
                />
            ) : undefined}
        </Box>
    );
};

export default PriceRequests;
